import { useRef, useState, useMemo, useEffect } from "react";
import DataGrid from '../../../components/common/dataGrid/dataGrid.js';
import ButtonWithSearch from '../../../components/common/buttonWithSearch.js';
import LinearColoredComponent from '../../../components/common/linearColoredComponent.js';

/**
 * PermissionRolesView Component
 * Displays a list of roles assigned to a specific permission
 * Shows when editing/viewing a permission to see which roles have it assigned
 * 
 * @param {Object} editPermission - The permission object being viewed/edited
 * @param {Function} onBack - Handler for back button click
 * @param {Object} permissionsData - Permission data object containing:
 *   - loading: Boolean for loading state
 *   - assignNum: Object for assigned numbers calculation
 */
export default function PermissionRolesView({
  editPermission,
  onBack,
  permissionsData,
}) {
  const menuLeft = useRef(null);

  // Transform role_names array into DataGrid format
  const allTransformedData = useMemo(() => 
    editPermission?.role_names?.map((item) => ({
      role_names: [item],
    })) || []
  , [editPermission?.role_names]);

  // Pagination state
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 0,
  });

  // Update totalRecords when data changes
  useEffect(() => {
    setNavigator((prev) => ({
      ...prev,
      totalRecords: allTransformedData.length,
      first: 0,
      page: 1,
    }));
  }, [allTransformedData.length]);

  // Client-side pagination - slice data based on current page and rows
  const transformedData = useMemo(() => {
    const startIndex = navigator.first;
    const endIndex = startIndex + navigator.rows;
    return allTransformedData.slice(startIndex, endIndex);
  }, [allTransformedData, navigator.first, navigator.rows]);

  // Handle page change
  const handlePageChange = (event) => {
    setNavigator((prev) => ({
      ...prev,
      first: event?.first || 0,
      page: (event?.page || 0) + 1,
    }));
  };

  // Handle row count change
  const handleRowCountChange = (e) => {
    setNavigator((prev) => ({
      ...prev,
      rows: parseInt(e?.target?.value) || 10,
      page: 1,
      first: 0,
    }));
  };

  return (
    <div className="mt-3">
      <DataGrid
        header={
          <ButtonWithSearch
            backButton={onBack}
            searchHeaderLable={"Permissions"}
            styles={{ gridTemplateColumns: "1fr 400px" }}
          />
        }
        isPaginatorEnabled={true}
        navigator={navigator}
        onPageChange={handlePageChange}
        onRowCountChange={handleRowCountChange}
        column={[{ label: "Assigned to", name: "role_names" }]}
        value={transformedData}
        ref={menuLeft}
        loading={permissionsData?.loading}
        customColumn={["role_names"]}
        columnCallBack={(data, title) => (
          <LinearColoredComponent
            data={data}
            title={title}
            assignNum={permissionsData?.assignNum}
          />
        )}
      />
    </div>
  );
}

